import { useMemo, useState } from "react";
import { DEFAULT_SCHEDULE_TEMPLATE_GROUP } from "@common/constants/domains/accounting/schedules";
import { useScheduleTemplates } from "@api/scheduleTemplate.api";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { PlusIcon } from "@icons";
import { NewScheduleGroupModal } from "./NewScheduleGroupModal";
import {
    DUPLICATE_SCHEDULE_GROUP_MESSAGE,
    isExistingScheduleGroupName,
} from "./scheduleGroupName";

interface ScheduleGroupComboboxProps {
    value: string;
    onChange: (group: string) => void;
}

export const ScheduleGroupCombobox: React.FC<ScheduleGroupComboboxProps> = ({
    value,
    onChange,
}) => {
    const templatesQuery = useScheduleTemplates();
    const [isNewGroupOpen, setIsNewGroupOpen] = useState(false);

    const groups = useMemo(() => {
        const names = new Set<string>([DEFAULT_SCHEDULE_TEMPLATE_GROUP]);
        for (const template of templatesQuery.data ?? []) {
            names.add(template.group);
        }
        if (value.trim() !== "") {
            names.add(value);
        }
        return [...names].sort((leftName, rightName) =>
            leftName.localeCompare(rightName),
        );
    }, [templatesQuery.data, value]);

    const items = useMemo<Array<PickerItemData<string>>>(
        () => groups.map((group) => ({ value: group, label: group })),
        [groups],
    );

    const renderFooter = (query: string) => {
        const trimmedQuery = query.trim();
        const hasExactMatch = isExistingScheduleGroupName(trimmedQuery, groups);
        if (hasExactMatch) {
            return (
                <Picker.Item disabled>
                    {DUPLICATE_SCHEDULE_GROUP_MESSAGE}
                </Picker.Item>
            );
        }
        return (
            <Picker.Item
                leading={<PlusIcon />}
                onSelect={() => {
                    if (trimmedQuery === "") {
                        setIsNewGroupOpen(true);
                        return;
                    }
                    onChange(trimmedQuery);
                }}
            >
                {trimmedQuery ? `Create "${trimmedQuery}"` : "Create group"}
            </Picker.Item>
        );
    };

    return (
        <>
            <Picker.Select
                items={items}
                searchable
                searchPlaceholder="Search groups..."
                emptyLabel="No matching groups"
                value={value === "" ? null : value}
                onValueChange={(group) => {
                    if (group != null) {
                        onChange(group);
                    }
                }}
                placeholder="Select group..."
                renderFooter={renderFooter}
            />
            <NewScheduleGroupModal
                show={isNewGroupOpen}
                onHide={() => setIsNewGroupOpen(false)}
                existingGroupNames={groups}
                onCreate={onChange}
            />
        </>
    );
};
